import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Exercise } from "../../exercise.interfaces";
import { getEditorForType } from '@/pages/Admin/Modules/Edit/exercises/editorsMap';
import { useState } from "react";

interface ExerciseCardProps {
  exercise: Exercise;
  onDelete?: () => void;
  onClick?: () => void;
  onActiveChange?: (isActive: boolean) => void;
  className?: string;
}

export const ExerciseCard = ({ 
  exercise, 
  onDelete,
  onClick,
  onActiveChange,
  className = ''
}: ExerciseCardProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Obtém o editor para o tipo do exercício
  const Editor = getEditorForType(exercise.type);

  // Obtém as informações formatadas usando os métodos do editor
  // @ts-expect-error - Tipos diferentes de exercício entre componentes e serviços
  const cardInfo = Editor.GetCardInfo(exercise);
  const IconComponent = Editor.getIcon();

  const handleDelete = () => {
    onDelete?.();
    setDeleteDialogOpen(false);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Não navegar se clicar em um botão
    if (e.target instanceof HTMLElement && e.target.closest('button')) {
      return;
    }
    onClick?.();
  };

  const isActive = exercise.isActive !== false;
  const cardClasses = `group transition-colors ${!isActive ? 'opacity-60 border-dashed' : ''} ${onClick ? 'cursor-pointer hover:border-blue-500' : ''} ${className}`;

  return (
    <>
      <Card className={cardClasses} onClick={handleCardClick}>
        <CardHeader className="flex-row items-center justify-between p-4">
          <div className="flex-grow">
            <div className="font-medium">{cardInfo.subtitle}</div>
            <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
              {IconComponent && <IconComponent className="w-4 h-4" />}
              <span>{Editor.getTitle()}</span>
              <span className="text-gray-400">•</span>
              {/* @ts-expect-error - Tipos diferentes de exercício entre componentes e serviços */}
              <span>{Editor.getDescription(exercise)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onActiveChange && (
              <div
                className="flex items-center gap-2"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <Switch
                  checked={isActive}
                  onCheckedChange={onActiveChange}
                  aria-label={isActive ? "Ativo" : "Inativo"}
                />
                <Label className="text-xs text-muted-foreground">
                  {isActive ? "Ativo" : "Inativo"}
                </Label>
              </div>
            )}
            {onDelete && (
              <Button
                variant="outline"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteDialogOpen(true);
                }}
                title="Excluir exercício"
                className="bg-gray-100"
              >
                <FontAwesomeIcon
                  icon={faTrashAlt}
                  className="h-4 w-4 text-gray-700"
                  style={{ display: "block" }}
                />
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            Tem certeza que deseja excluir este exercício? Esta ação não pode ser desfeita.
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
